import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Search, RefreshCw, Satellite, AlertCircle, Loader2, Monitor,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/lib/auth';
import { useFleetLive, useAutoMap, type FleetLiveItem } from '@/hooks/useFleetLive';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api';

function formatTimeSince(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const d = Math.floor(hr / 24);
  return `${d} day${d > 1 ? 's' : ''} ago`;
}

function formatKm(km: number | null): string {
  if (km == null) return '—';
  return `${km.toFixed(2)} km`;
}

function formatSpeed(kmh: number | null): string {
  if (kmh == null) return '—';
  return `${kmh.toFixed(0)} km/h`;
}

export default function FleetLivePage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useFleetLive();
  const autoMap = useAutoMap();

  // PR W2: today's idle waste per vehicle (best-effort — empty if no data)
  const [idleToday, setIdleToday] = useState<Record<string, { wasteCostSar: number; wastedMinutes: number }>>({});
  useEffect(() => {
    let cancelled = false;
    apiClient.get<Record<string, { wasteCostSar: number; wastedMinutes: number }>>('/api/v1/idle-detection/idle-waste/today-by-vehicle')
      .then((r) => { if (!cancelled) setIdleToday(r.data ?? {}); })
      .catch(() => { if (!cancelled) setIdleToday({}); });
    return () => { cancelled = true; };
  }, [dataUpdatedAt]);

  const items = data?.items ?? [];
  const tracked = data?.tracked ?? 0;
  const total = data?.total ?? 0;

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((v) =>
      [v.plateEn, v.plateAr, v.code, v.investorName, v.currentSiteName, v.afaqyUnitName]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'fleet_officer';

  return (
    <div className="min-h-screen w-full bg-background flex flex-col" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Logo size={28} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-base leading-none">
                {t('fleetLive.title', 'Live Fleet Tracking')}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t('fleetLive.subtitle', 'Real-time vehicle locations from Afaqy GPS')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard/fleet/control-tower"
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/50"
            >
              <Monitor className="h-4 w-4" />
              Control Tower
            </Link>
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* Status bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Satellite className="h-5 w-5 text-sky-500" />
            <div>
              <div className="text-sm font-medium">
                {tracked} / {total} {t('fleetLive.tracked', 'tracked')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('fleetLive.lastSync', 'Last refresh')}:{' '}
                {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('fleetLive.searchPlaceholder', 'Search plate, site, investor...')}
                className="ps-9 w-64"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              {t('common.refresh', 'Refresh')}
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  autoMap.mutate(undefined, {
                    onSuccess: (r) =>
                      alert(t('fleetLive.autoMapResult', { count: r.newlyMapped, defaultValue: `Mapped ${r.newlyMapped} new vehicle(s)` })),
                  })
                }
                disabled={autoMap.isPending}
              >
                {autoMap.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('fleetLive.autoMap', 'Auto-map')
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Loading / error / empty states */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-6 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
            <div className="text-destructive font-medium">
              {t('fleetLive.error', 'Could not load fleet data')}
            </div>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
              {t('common.retry', 'Retry')}
            </Button>
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-10 text-center">
            <Satellite className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <div className="font-medium">
              {search
                ? t('fleetLive.noMatches', 'No vehicles match your search')
                : t('fleetLive.empty', 'No vehicles available for tracking yet')}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {!search &&
                t(
                  'fleetLive.emptyHint',
                  'Add vehicles in Jasr Hub and they will auto-link to Afaqy by plate number.',
                )}
            </div>
          </div>
        )}

        {/* Table */}
        {!isLoading && !isError && filtered.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-start px-4 py-3 font-medium">
                      {t('fleetLive.cols.plate', 'Plate')}
                    </th>
                    <th className="text-start px-4 py-3 font-medium">
                      {t('fleetLive.cols.investor', 'Investor')}
                    </th>
                    <th className="text-start px-4 py-3 font-medium">
                      {t('fleetLive.cols.site', 'Site')}
                    </th>
                    <th className="text-start px-4 py-3 font-medium">
                      {t('fleetLive.cols.lastUpdate', 'Last update')}
                    </th>
                    <th className="text-end px-4 py-3 font-medium">
                      {t('fleetLive.cols.speed', 'Speed')}
                    </th>
                    <th className="text-end px-4 py-3 font-medium">
                      {t('fleetLive.cols.yesterday', 'Yesterday')}
                    </th>
                    <th className="text-end px-4 py-3 font-medium">Today idle</th>
                    <th className="text-center px-4 py-3 font-medium">
                      {t('fleetLive.cols.location', 'Location')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v) => (
                    <FleetRow key={v.vehicleId} v={v} t={t} idle={idleToday[v.vehicleId]} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function FleetRow({ v, t, idle }: { v: FleetLiveItem; t: ReturnType<typeof useTranslation>['t']; idle: { wasteCostSar: number; wastedMinutes: number } | undefined }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30">
      <td className="px-4 py-3">
        <div className="font-medium font-mono">{v.plateEn ?? v.code}</div>
        {v.plateAr && (
          <div className="text-xs text-muted-foreground" dir="rtl">
            {v.plateAr}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{v.investorName ?? '—'}</td>
      <td className="px-4 py-3 text-muted-foreground">{v.currentSiteName ?? '—'}</td>
      <td className="px-4 py-3">
        {v.isTracked ? (
          <span className="text-foreground">{formatTimeSince(v.lastUpdateAt)}</span>
        ) : (
          <Badge variant="inactive" className="text-xs">
            {t('fleetLive.notTracked', 'No GPS')}
          </Badge>
        )}
      </td>
      <td className="px-4 py-3 text-end font-mono tabular-nums">
        {formatSpeed(v.lastSpeedKmh)}
      </td>
      <td className="px-4 py-3 text-end font-mono tabular-nums text-muted-foreground">
        {formatKm(v.yesterdayKmDriven)}
      </td>
      <td className="px-4 py-3 text-end font-mono tabular-nums">
        {idle && idle.wastedMinutes > 0 ? (
          <span className="text-amber-700 dark:text-amber-400">{Math.round(idle.wastedMinutes)} min · SAR {idle.wasteCostSar.toFixed(2)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {v.googleMapsUrl ? (
          <a
            href={v.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
          >
            <MapPin className="h-4 w-4" />
            {t('fleetLive.openMap', 'Open in Maps')}
          </a>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </td>
    </tr>
  );
}
